import { DataSource } from "@inrev/common";
import { useOnClickOutside } from "@portal/hooks/useOnClickOutside";
import { type FC, useRef, useState } from "react";
import { isPriorPeriodOnStatement } from "../../financials/utils/isPriorPeriodOnStatement";
import { cn } from "../../lib/utils";
import { ClickableIcon } from "../../primitives";
import { ManualPeriodIndicator } from "../Financials/ManualPeriodIndicator";
import { PeriodReviewedIndicator } from "../Financials/PeriodReviewedIndicator";
import { PriorPeriodIndicator } from "../Financials/PriorPeriodIndicator";
import type { PeriodViewModel } from "./types";

type PeriodHeaderProps = {
	period: PeriodViewModel;
	contextMenu?: React.ReactNode;
};

export const PeriodHeader: FC<PeriodHeaderProps> = ({ period, contextMenu }) => {
	const menuRef = useRef<HTMLSpanElement>(null);
	const [showMenu, setShowMenu] = useState(false);

	useOnClickOutside(menuRef, () => setShowMenu(false), Boolean(contextMenu) && showMenu);

	const isPriorPeriod = isPriorPeriodOnStatement(period.periodEndDate, period.sourceStatementDate);

	return (
		<div
			data-testid="period-header"
			className="flex items-center justify-center px-4 py-3 group h-full relative"
		>
			<div className="flex flex-col items-center justify-center">
				<div className="flex items-center gap-1">
					{period.reviewedAt && <PeriodReviewedIndicator reviewDate={period.reviewedAt} />}
					<span className="text-sm font-semibold text-primary text-center">
						{period.label}
						{period.sourceDataSource === DataSource.manual && <ManualPeriodIndicator />}
					</span>
				</div>
				<span className="flex items-center justify-center text-xs text-tertiary font-semibold">
					{period.periodTypeLabel}
					{isPriorPeriod && period.sourceStatementDate && (
						<PriorPeriodIndicator sourceStatementDate={period.sourceStatementDate} />
					)}
				</span>
			</div>
			{contextMenu && (
				<span ref={menuRef} className="absolute right-2 top-1/2 -translate-y-1/2">
					<ClickableIcon
						data-testid="period-header-menu-button"
						type="dots-vertical"
						size="small"
						color="tertiary"
						onClick={() => setShowMenu((prev) => !prev)}
						className={cn(
							"hidden group-hover:flex items-center justify-center w-6 h-6 rounded hover:bg-surface-light",
							showMenu && "flex",
						)}
					/>
					{showMenu && contextMenu}
				</span>
			)}
		</div>
	);
};
